import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';

import {
  relative,
  textTransform,
} from '../../utils/helpers';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import { updateCard, cardData } from '../../redux/Slices/cardSlice';
import MemberImage from '../Global/MemberImage';
import DragIndicator from '../icons/DragIndicator';
import EditIcon from '../icons/Edit';
import ModeComment from '../icons/ModeComment';
import MoreHoriz from '../icons/MoreHoriz';
import CircleCheck from '../icons/CircleCheck';
import Brightness1 from '../icons/Brightness1';
import Person from '../icons/Person';
import AttachFile from '../icons/AttachFile';
import Checklist from '../icons/Checklist';

const ListViewCardRow = ({
  card,
  list,
  moveCard,
  setBoard,
  isReadOnly,
  visibleColumns,
  boardId,
  boardRef,
}) => {
  const rowRef = useRef(null);
  const [isDragging, setDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { handlePopoverClick } = useHandlePopoverClick();

  // Enrich card with board shortId so popovers (labels, members, etc.) can resolve the board
  const enrichedCard = card?.board ? card : { ...card, board: boardId };

  const [isCompleted, setIsCompleted] = useState(card?.completed || false);

  const {
    _id,
    title,
    permalink,
    users,
    comments,
    commentCount,
    actionList,
    shortId,
    order,
    priority,
    dueDate,
    labels,
    checklists,
    attachments,
    createdAt,
    isOptimistic,
    isOptimisticallyMoved,
  } = card || {};

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(title || '');
  const editInputRef = useRef(null);

  useEffect(() => {
    setIsCompleted(card?.completed || false);
  }, [card?.completed]);

  useEffect(() => {
    setEditTitle(title || '');
  }, [title]);

  const members = users || [];
  const priorityText = textTransform(priority, 'lowercase');
  const cardLabels = labels || [];
  const cardAttachments = attachments || [];
  const cardChecklists = checklists || [];
  const checklistTotal = cardChecklists.reduce((sum, cl) => sum + (cl.items?.length || 0), 0);
  const checklistChecked = cardChecklists.reduce((sum, cl) => sum + (cl.items?.filter((i) => i.checked).length || 0), 0);
  const formattedCreatedAt = createdAt ? new Date(createdAt).toLocaleDateString('default', { month: 'short', day: 'numeric' }) : null;

  // Format due date
  const formatDueDate = (dueDate) => {
    if (!dueDate?.date) return null;
    const date = new Date(dueDate.date);
    const now = new Date();
    const isOverdue = date < now && !dueDate.completed;
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return { text: `${month} ${day}`, isOverdue, completed: dueDate.completed };
  };

  const dueDateInfo = formatDueDate(dueDate);

  // Open card detail
  const handleOpenCard = useCallback((e) => {
    e.stopPropagation();
    if (permalink) {
      navigate(relative(permalink));
    }
  }, [permalink, navigate]);

  // Mark as complete handler
  const handleCardCompletion = useCallback(async (e) => {
    e.stopPropagation();
    const newState = !isCompleted;
    setIsCompleted(newState);

    setBoard((prev) => ({
      ...prev,
      actionLists: prev.actionLists.map((al) => ({
        ...al,
        cards: (al.cards || []).map((c) =>
          c._id === _id ? { ...c, completed: newState } : c
        ),
      })),
    }));

    try {
      await dispatch(updateCard({ cardId: shortId, updatedData: { completed: newState } })).unwrap();
    } catch (err) {
      setIsCompleted(!newState);
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((al) => ({
          ...al,
          cards: (al.cards || []).map((c) =>
            c._id === _id ? { ...c, completed: !newState } : c
          ),
        })),
      }));
    }
  }, [isCompleted, _id, shortId, dispatch, setBoard]);

  // Action menu handler
  const handleActionMenu = useCallback((e) => {
    e.stopPropagation();
    handlePopoverClick(e, 'cardActions', {
      cardId: shortId,
      card,
    });
  }, [shortId, card, handlePopoverClick]);

  // Column action handlers — open relevant popovers
  // Popovers read card from Redux state.card, so we must set it first
  const handleMembersClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'cardMemberesPopover', { cardId: shortId });
  }, [shortId, enrichedCard, isReadOnly, dispatch, handlePopoverClick]);

  const handleDueDateClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'addDueDate', { cardId: shortId });
  }, [shortId, enrichedCard, isReadOnly, dispatch, handlePopoverClick]);

  const handlePriorityClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'cardPriority', { cardId: shortId, isClosed: false });
  }, [shortId, enrichedCard, isReadOnly, dispatch, handlePopoverClick]);

  const handleStatusClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'moveCard', { cardId: shortId, card });
  }, [shortId, enrichedCard, card, isReadOnly, dispatch, handlePopoverClick]);

  const handleLabelsClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'addLabels', { cardId: shortId, card });
  }, [shortId, enrichedCard, card, isReadOnly, dispatch, handlePopoverClick]);

  const handleChecklistClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'addChecklist', { cardId: shortId });
  }, [shortId, enrichedCard, isReadOnly, dispatch, handlePopoverClick]);

  const handleAttachmentClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    dispatch(cardData(enrichedCard));
    handlePopoverClick(e, 'attachment', { cardId: shortId, card });
  }, [shortId, enrichedCard, card, isReadOnly, dispatch, handlePopoverClick]);

  // Inline title edit
  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    setIsEditingTitle(true);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, [isReadOnly]);

  const saveTitle = useCallback(async () => {
    const trimmed = editTitle.trim();
    setIsEditingTitle(false);
    if (!trimmed || trimmed === title) {
      setEditTitle(title || '');
      return;
    }

    // Optimistic update
    setBoard((prev) => ({
      ...prev,
      actionLists: prev.actionLists.map((al) => ({
        ...al,
        cards: (al.cards || []).map((c) =>
          c._id === _id ? { ...c, title: trimmed } : c
        ),
      })),
    }));

    try {
      await dispatch(updateCard({ cardId: shortId, updatedData: { title: trimmed } })).unwrap();
    } catch {
      setEditTitle(title || '');
      setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((al) => ({
          ...al,
          cards: (al.cards || []).map((c) =>
            c._id === _id ? { ...c, title } : c
          ),
        })),
      }));
    }
  }, [editTitle, title, _id, shortId, dispatch, setBoard]);

  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle();
    } else if (e.key === 'Escape') {
      setEditTitle(title || '');
      setIsEditingTitle(false);
    }
  }, [saveTitle, title]);

  // Drag and Drop setup
  useEffect(() => {
    const element = rowRef.current;
    if (!element || isReadOnly || isOptimistic) return;
    if (!card || !card._id || !card.actionList) return;
    // Don't attach drag handlers while the title is being edited inline —
    // otherwise dragging across the input field steals focus and breaks
    // typing.
    if (isEditingTitle) return;

    const combined = combine(
      draggable({
        element,
        dragHandle: element,
        getInitialData: () => ({ ...card, type: 'CARD' }),
        onGenerateDragPreview({ nativeSetDragImage, location }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element, input: location.current.input }),
            render({ container }) {
              const preview = element.cloneNode(true);
              preview.style.width = `${element.getBoundingClientRect().width}px`;
              preview.style.opacity = '0.9';
              preview.style.transform = 'rotate(1deg)';
              preview.style.background = 'var(--primary-background)';
              preview.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
              preview.style.borderRadius = '6px';
              container.appendChild(preview);
            },
          });
        },
        onDragStart() {
          setDragging(true);
        },
        onDrop() {
          setDragging(false);
          setClosestEdge(null);
        },
      }),
      dropTargetForElements({
        element,
        getIsSticky: () => true,
        getData: ({ input, element }) => {
          return attachClosestEdge({ ...card, type: 'CARD' }, {
            input,
            element,
            allowedEdges: ['top', 'bottom'],
            threshold: 0.3,
          });
        },
        // Cards no longer reorder while the user is dragging — only the
        // closest-edge indicator (blue line) follows the cursor. The
        // actual move is performed on drop in BoardListView's
        // monitorForElements handler.
        onDrag(args) {
          const { source, self } = args;
          if (!self || !source) return;
          if (self.data._id === source.data._id && source.data.type === 'CARD') return;
          if (source.data.type !== 'CARD') return;

          const edge = extractClosestEdge(self.data);
          if (!edge) return;
          setClosestEdge(edge);
        },
        onDragLeave() {
          setClosestEdge(null);
        },
        onDrop() {
          setClosestEdge(null);
          setDragging(false);
        },
      })
    );

    return () => combined();
  }, [card?._id, card?.order, card?.actionList, moveCard, isReadOnly, isOptimistic, setBoard, card, boardRef, isEditingTitle]);

  // Globally clear the drop-edge indicator on any drop completion. The
  // per-target onDragLeave / onDrop hooks miss edge cases (e.g. fast
  // cross-list drops), occasionally leaving a stale blue line on a row
  // the card was dragged through.
  useEffect(() => {
    return monitorForElements({
      onDrop: () => setClosestEdge(null),
    });
  }, []);

  const getRowClass = () => {
    const classes = ['list-view-card-row'];
    if (isDragging) classes.push('dragging');
    if (isOptimistic) classes.push('optimistic');
    if (isOptimisticallyMoved) classes.push('optimistically-moved');
    if (isCompleted) classes.push('completed');
    if (closestEdge === 'top') classes.push('drop-indicator-top');
    if (closestEdge === 'bottom') classes.push('drop-indicator-bottom');
    return classes.join(' ');
  };

  return (
    <div
      className={getRowClass()}
      ref={rowRef}
      style={isOptimistic ? { pointerEvents: 'none', opacity: 0.6 } : {}}
    >


      {/* Frozen left section: drag handle + complete + title */}
      <div className="row-frozen">
        {!isReadOnly && (
          <span className="row-drag-handle">
            <DragIndicator size={16} color="var(--secondary-text-color)" />
          </span>
        )}

        <span
          className={`row-complete-btn${isCompleted ? ' completed' : ''}`}
          onClick={handleCardCompletion}
          data-tooltip-content={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
          data-tooltip-position="top"
        >
          {isCompleted ? (
            <CircleCheck size={18} color="var(--secondary-brand-color)" />
          ) : (
            <Brightness1 size={18} color="var(--secondary-text-color)" />
          )}
        </span>

        <div className="row-title">
          {isEditingTitle ? (
            <input
              ref={editInputRef}
              className="row-title-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={handleEditKeyDown}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <span className="row-title-text" onClick={handleOpenCard}>{title}</span>
              {!isReadOnly && (
                <span
                  className="row-title-edit-icon"
                  onClick={handleEditClick}
                  data-tooltip-content="Edit title"
                  data-tooltip-position="top"
                >
                  <EditIcon size={14} color="var(--secondary-text-color)" />
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Assignee */}
      {visibleColumns.has('members') && (
        <div
          className="row-members"
          onClick={handleMembersClick}
          id={`popover-lv-members-${_id}`}
          data-popover-trigger
        >
          {members.length > 0 ? (
            <MemberImage cardId={_id} members={members} type="card" size={24} isReadOnly={true} />
          ) : (
            <span className="row-placeholder-icon">
              <Person size={16} color="var(--secondary-text-color)" />
            </span>
          )}
        </div>
      )}

      {/* Due date */}
      {visibleColumns.has('dueDate') && (
        <div
          className="row-due-date"
          onClick={handleDueDateClick}
          id={`popover-lv-duedate-${_id}`}
          data-popover-trigger
        >
          {dueDateInfo ? (
            <span className={`due-date-badge${dueDateInfo.isOverdue ? ' overdue' : ''}${dueDateInfo.completed ? ' completed' : ''}`}>
              {dueDateInfo.text}
            </span>
          ) : (
            <span className="row-placeholder-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 -960 960 960" fill="var(--secondary-text-color)">
                <path d="M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Z" />
              </svg>
            </span>
          )}
        </div>
      )}

      {/* Priority */}
      {visibleColumns.has('priority') && (
        <div
          className="row-priority"
          onClick={handlePriorityClick}
          id={`popover-lv-priority-${_id}`}
          data-popover-trigger
        >
          <svg
            className={`zoobbe-flag-${priorityText || 'normal'}`}
            xmlns="http://www.w3.org/2000/svg"
            height="16px"
            viewBox="0 0 24 24"
            width="16px"
            fill={priority && priority !== 'Normal' ? undefined : 'var(--muted-text-color)'}
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M14.4 6l-.24-1.2c-.09-.46-.5-.8-.98-.8H6c-.55 0-1 .45-1 1v15c0 .55.45 1 1 1s1-.45 1-1v-6h5.6l.24 1.2c.09.47.5.8.98.8H19c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1h-4.6z" />
          </svg>
        </div>
      )}

      {/* Status */}
      {visibleColumns.has('status') && (
        <div
          className="row-status"
          onClick={handleStatusClick}
          id={`popover-lv-status-${_id}`}
          data-popover-trigger
        >
          <span className="status-badge">
            <span
              className="status-dot"
              style={{ backgroundColor: list?.color || 'var(--brand-color)' }}
            />
            <span className="status-text">{list?.title || ''}</span>
          </span>
        </div>
      )}

      {/* Labels */}
      {visibleColumns.has('labels') && (
        <div
          className="row-labels"
          onClick={handleLabelsClick}
          id={`popover-lv-labels-${_id}`}
          data-popover-trigger
        >
          {cardLabels.length > 0 ? (
            <div className="labels-list">
              {cardLabels.slice(0, 3).map((label, i) => (
                <span
                  key={i}
                  className="label-pill"
                  style={{ backgroundColor: label.color || 'var(--muted-text-color)' }}
                />
              ))}
              {cardLabels.length > 3 && (
                <span className="labels-more">+{cardLabels.length - 3}</span>
              )}
            </div>
          ) : (
            <span className="row-placeholder-icon">
              <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 -960 960 960" fill="var(--secondary-text-color)">
                <path d="M840-480 666-234q-11 16-28.5 25t-37.5 9H200q-33 0-56.5-23.5T120-280v-400q0-33 23.5-56.5T200-760h400q20 0 37.5 9t28.5 25l174 246Zm-88 0L600-680H200v400h400l152-200Z" />
              </svg>
            </span>
          )}
        </div>
      )}

      {/* Checklist */}
      {visibleColumns.has('checklist') && (
        <div
          className="row-checklist"
          onClick={handleChecklistClick}
          id={`popover-lv-checklist-${_id}`}
          data-popover-trigger
        >
          {checklistTotal > 0 ? (
            <span className={`checklist-progress${checklistChecked === checklistTotal ? ' all-done' : ''}`}>
              <Checklist size={14} color={checklistChecked === checklistTotal ? '#61bd4f' : 'var(--muted-text-color)'} />
              <span>{checklistChecked}/{checklistTotal}</span>
            </span>
          ) : (
            <span className="row-placeholder-icon">
              <Checklist size={14} color="var(--secondary-text-color)" />
            </span>
          )}
        </div>
      )}

      {/* Attachments */}
      {visibleColumns.has('attachments') && (
        <div
          className="row-attachments"
          onClick={handleAttachmentClick}
          id={`popover-lv-attachment-${_id}`}
          data-popover-trigger
        >
          {cardAttachments.length > 0 ? (
            <span className="attachments-count">
              <AttachFile size={14} color="var(--secondary-text-color)" />
              <span>{cardAttachments.length}</span>
            </span>
          ) : (
            <span className="row-placeholder-icon">
              <AttachFile size={14} color="var(--secondary-text-color)" />
            </span>
          )}
        </div>
      )}

      {/* Comments — opens the card */}
      {visibleColumns.has('comments') && (
        <div className="row-comments" onClick={handleOpenCard}>
          <ModeComment size={14} color="var(--secondary-text-color)" />
          <span className="comments-count">{commentCount || comments?.length || 0}</span>
        </div>
      )}

      {/* Created date */}
      {visibleColumns.has('createdAt') && (
        <div className="row-created-at" onClick={(e) => e.stopPropagation()}>
          {formattedCreatedAt ? (
            <span className="created-date-text">{formattedCreatedAt}</span>
          ) : (
            <span className="row-placeholder-icon">&mdash;</span>
          )}
        </div>
      )}

      {/* Action menu */}
      {!isReadOnly && (
        <span
          className="row-action-menu"
          onClick={handleActionMenu}
          id={`list-view-card-actions-${_id}`}
          data-popover-trigger
        >
          <MoreHoriz size={18} color="var(--secondary-text-color)" />
        </span>
      )}
    </div>
  );
};

export default ListViewCardRow;
